import { useTableActionContext } from '@aibees/elephant/context';
import { useLabelGroupingStatus } from '@aibees/elephant/hooks/labels/use-label-grouping-status';
import { useGetCreditsAmountTypeOnActionPopup } from '@aibees/elephant/hooks/use-get-credits-amount-type-on-action-popup';
import { LabelType } from '@aibees/elephant/types/label';
import {
  DIALOG_IDS,
  getAccordionIdWithType,
  useTableAccordionStore,
} from '@aibees/shared/store/table-accordion-store';
import { useWorkspace } from '@aibees/shared/workspace';
import { useCallback, useEffect, useMemo } from 'react';

export function useAddListDialog() {
  // Context hooks
  const {
    handleAddToList,
    ids,
    lists,
    listDialogState,
    handleClose,
    previousState,
    setPreviousState,
    effectiveSearchType,
  } = useTableActionContext();
  // Use the flow's effective search type (folds in the ActionButton override) so a
  // newly created list matches the record's type, not just the page route.
  const grouping = useLabelGroupingStatus(effectiveSearchType);

  // Credit types
  const {
    aiCompany,
    aiPeople,
    aiUnsavedPeople,
    basicPeople,
    basicUnsavedPeople,
  } = useGetCreditsAmountTypeOnActionPopup();

  // Other hooks
  const workspace = useWorkspace();

  // Memoized values
  const showAiLookalike = useMemo(
    () =>
      aiCompany ||
      aiPeople ||
      aiUnsavedPeople ||
      basicPeople ||
      basicUnsavedPeople,
    [aiCompany, aiPeople, aiUnsavedPeople, basicPeople, basicUnsavedPeople]
  );

  // Use table accordion store directly
  const {
    getDialogExpanded,
    setDialogExpanded,
    setInitialExpanded,
    clearDialog,
  } = useTableAccordionStore();

  const dialogId = DIALOG_IDS.ADD_LIST_DIALOG;
  const expandedAccordion = getDialogExpanded(dialogId);

  // Determine initial expanded accordion based on previousState
  const getInitialAccordionId = useCallback(() => {
    if (previousState === LabelType.LIST) {
      return getAccordionIdWithType('LIST');
    }
    return getAccordionIdWithType('LIST');
  }, [previousState]);

  // Set initial expanded accordion when dialog opens
  useEffect(() => {
    if (listDialogState.value) {
      setInitialExpanded(dialogId, getInitialAccordionId());
    }
  }, [
    listDialogState.value,
    dialogId,
    setInitialExpanded,
    getInitialAccordionId,
  ]);

  // Clear dialog state when dialog closes
  useEffect(() => {
    if (!listDialogState.value) {
      clearDialog(dialogId);
    }
  }, [listDialogState.value, clearDialog, dialogId]);

  // Handlers
  const setExpandedAccordion = useCallback(
    (accordionId: string | null) => {
      setDialogExpanded(dialogId, accordionId);
    },
    [dialogId, setDialogExpanded]
  );

  const handleDialogClose = useCallback(() => {
    handleClose('list');
    setPreviousState('');
  }, [handleClose, setPreviousState]);

  return {
    // State
    listDialogState,
    ids,
    lists,
    expandedAccordion,
    showAiLookalike,
    workspace,
    grouping,
    setPreviousState,
    // Handlers
    handleAddToList,
    handleClose,
    handleDialogClose,
    setExpandedAccordion,

    // Computed values
    dialogId,
    hasIds: ids.length > 0,
    hasLists: lists.length > 0,
  };
}
